import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { GridColDef } from '@mui/x-data-grid';
import { selectMyInfo } from '@/pages/auth/authSlice';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { MemberDuplicationCheckDTO } from '@/api/DTO/member-management/memberDuplication';
import {
	MatchedNameCell,
	MatchedText,
	ViewRecordCell,
} from './DuplicationCells';

interface Props {
	open: boolean;
	rows: MemberDuplicationCheckDTO[];
	/** `true` = staff confirmed "not the same person"; `false` = cancelled. */
	onClose: (confirmed: boolean) => void;
}

/**
 * Step 1 of the member duplication check (DMS-REQ-WEB-CHA-002 §3.3):
 * in-progress records that match. Staff must explicitly confirm the applicant
 * is not the same person when a record of their current centre is hit.
 * Records of other centres are for information only (§3.5): the same person
 * being served by another centre is normal.
 */
export default function InProgressDuplicateDialog({
	open,
	rows,
	onClose,
}: Props) {
	const { t } = useTranslation();
	const userInfo = useSelector(selectMyInfo);
	const [confirmed, setConfirmed] = useState(false);
	const currentBranchId = userInfo?.currentBranch?.id;
	const needsConfirm = rows.some((row) => row.branchId === currentBranchId);

	useEffect(() => {
		if (open) setConfirmed(false);
	}, [open]);

	const columns: GridColDef<MemberDuplicationCheckDTO>[] = [
		{ field: 'branchName', headerName: t('member.unit'), flex: 12 },
		{
			field: 'preAdmissionNo',
			headerName: t('member.preAdmissionNo'),
			flex: 15,
			renderCell: (params) => params.row.preAdmissionNo || '-',
		},
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 15,
			renderCell: (params) => params.row.fileNo || '-',
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 18,
			renderCell: (params) => (
				<MatchedNameCell
					nameEn={params.row.nameEn}
					nameZh={params.row.nameZh}
					matchedFields={params.row.matchedFields}
				/>
			),
		},
		{
			field: 'phone',
			headerName: t('member.mobilePhone'),
			flex: 12,
			renderCell: (params) => (
				<MatchedText
					value={params.row.phone}
					field='PHONE'
					matchedFields={params.row.matchedFields}
				/>
			),
		},
		{
			field: 'hkid',
			headerName: t('member.HKID'),
			flex: 12,
			renderCell: (params) => (
				<MatchedText
					value={params.row.hkid}
					field='HKID'
					matchedFields={params.row.matchedFields}
					hidden={!params.row.canSeen}
				/>
			),
		},
		{
			field: 'statusDesc',
			headerName: t('member.status'),
			flex: 18,
			renderCell: (params) =>
				params.row.statusDesc || params.row.preAdmissionStatusDesc || '-',
		},
		{
			field: 'view',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<ViewRecordCell
					id={params.row.id}
					status={params.row.status}
					canSeen={params.row.canSeen}
				/>
			),
		},
	];

	return (
		<Dialog open={open} onClose={() => onClose(false)} maxWidth='lg' fullWidth>
			<DialogTitle variant='h5'>
				{needsConfirm
					? t('memberDuplication.inProgressTitle')
					: t('memberDuplication.otherUnitTitle')}
			</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={() => onClose(false)}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<CloseIcon />
			</IconButton>
			<DialogContent>
				<Typography variant='body2' sx={{ marginBottom: '16px' }}>
					{needsConfirm
						? t('memberDuplication.inProgressText')
						: t('memberDuplication.otherUnitText')}
				</Typography>
				<LocalDataGrid
					rows={rows}
					columns={columns}
					getRowId={(row) => row.id}
					pagination={false}
				/>
				{needsConfirm && (
					<FormControlLabel
						sx={{ marginTop: '16px' }}
						control={
							<Checkbox
								checked={confirmed}
								onChange={(e) => setConfirmed(e.target.checked)}
							/>
						}
						label={t('memberDuplication.notSamePersonConfirm')}
					/>
				)}
			</DialogContent>
			<DialogActions>
				<Button sx={{ textTransform: 'none' }} onClick={() => onClose(false)}>
					{t('button.cancel')}
				</Button>
				<Button
					sx={{ textTransform: 'none' }}
					variant='contained'
					disabled={needsConfirm && !confirmed}
					onClick={() => onClose(true)}
				>
					{t('button.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
